import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import TimeEntryAggregateCollection from '../State/Aggregates/TimeEntryAggregateCollection'
import tuple from 'immutable-tuple'
import { FormatNumber } from '../Utils/Localisation/NumberFormatter'
import { FormatPercent } from '../Utils/Localisation/PercentFormatter'
import RenderOnQueries from '../Pages/Layout/RenderOnQueries'
import {
    addDays,
    endOfISOWeek,
    endOfWeek,
    format,
    formatISO,
    startOfISOWeek,
} from 'date-fns'
import { qf } from '../Queries/queryFormatter'
import FetchStore from '../Queries/FetchStore'
import ProjectCollection from '../State/Collections/ProjectCollection'
import { Progress } from './ui/Progress'
import { Selector } from './Selector'
import { processTRPCData } from '../Queries/fetchData'
import { trpc } from '../system/trpc'

const budgetDataRequirements = ({
    projects,
    phase,
    staff,
    role,
    startDate,
    endDate,
    extraHours,
}) => {
    return [
        {
            id: 'budgetTimeEntries' + endDate,
            queryFn: async () => {
                const budgetData = await trpc.legacy.getProjectBudgets.query({
                    projectIds: projects.filter(Boolean).map((p) => p.id),
                    startDate: format(startDate, 'yyyy-MM-dd'),
                    endDate: format(endDate, 'yyyy-MM-dd'),
                    staffId: staff?.id,
                })
                return processTRPCData(
                    budgetData,
                    'budgetTimeEntries' + endDate
                )
            },
            staleTime: 0,
            cacheTime: 0,
        },
    ]
}

const budgetLookup = {
    phaseBudgetForTeam: ({
        projects,
        project,
        phase,
        staff,
        role,
        startDate,
        endDate,
        extraHours,
    }) => ({
        numerator:
            FetchStore.getResponse('budgetTimeEntries' + endDate)?.budgets?.[
                project?.id
            ]?.[phase?.id]?.teamBudgetUse + extraHours,
        denominator:
            FetchStore.getResponse('budgetTimeEntries' + endDate)?.budgets?.[
                project?.id
            ]?.[phase?.id]?.phaseBudget || 0,
    }),
    phaseBudgetForRole: ({
        projects,
        project,
        phase,
        staff,
        role,
        startDate,
        endDate,
        extraHours,
    }) => ({
        numerator:
            FetchStore.getResponse('budgetTimeEntries' + endDate)?.budgets?.[
                project?.id
            ]?.[phase?.id]?.roleBudgetUse + extraHours,
        denominator:
            FetchStore.getResponse('budgetTimeEntries' + endDate)?.budgets?.[
                project?.id
            ]?.[phase?.id]?.rolePhaseBudget || 0,
    }),
    phaseBudgetForStaff: ({
        projects,
        project,
        phase,
        staff,
        role,
        startDate,
        endDate,
        extraHours,
    }) => ({
        numerator:
            FetchStore.getResponse('budgetTimeEntries' + endDate)?.budgets?.[
                project?.id
            ]?.[phase?.id]?.staffBudgetUse + extraHours,
        denominator:
            FetchStore.getResponse('budgetTimeEntries' + endDate)?.budgets?.[
                project?.id
            ]?.[phase?.id]?.staffPhaseBudget || 0,
    }),
    monthlyPhaseBudgetForStaff: ({
        projects,
        project,
        phase,
        staff,
        role,
        startDate,
        endDate,
        extraHours,
    }) => ({
        numerator:
            FetchStore.getResponse('budgetTimeEntries' + endDate)?.budgets?.[
                project?.id
            ]?.[phase?.id]?.monthlyBudgetUse + extraHours,
        denominator:
            FetchStore.getResponse('budgetTimeEntries' + endDate)?.budgets?.[
                project?.id
            ]?.[phase?.id]?.monthlyAllocation || 0,
    }),
    weeklyPhaseBudgetForStaff: ({
        projects,
        project,
        phase,
        staff,
        role,
        startDate,
        endDate,
        extraHours,
    }) => {
        startDate = startOfISOWeek(startDate)
        return {
            numerator:
                FetchStore.getResponse('budgetTimeEntries' + endDate)
                    ?.budgets?.[project?.id]?.[phase?.id]?.weeklyBudgetUse +
                extraHours,
            denominator:
                FetchStore.getResponse('budgetTimeEntries' + endDate)
                    ?.budgets?.[project?.id]?.[phase?.id]?.weeklyAllocation ||
                0,
        }
    },
}

export const budgetNames = {
    phaseBudgetForTeam: 'Phase Budget for Team',
    phaseBudgetForRole: 'Phase Budget for Role',
    phaseBudgetForStaff: 'Phase Budget for Staff Member',
    monthlyPhaseBudgetForStaff: 'Monthly Phase Budget for Staff Member',
    weeklyPhaseBudgetForStaff: 'Weekly Phase Budget for Staff Member',
}

export default observer(
    ({
        project,
        projects,
        phase,
        staff,
        role,
        startDate,
        endDate,
        budget,
        setBudget,
        extraHours = 0,
        textStyles = {},
        barStyles = {},
        dropdownStyles = {},
        containerStyles = {},
        dropdownAbove = false,
        dropdownClassName,
        variant,
        ...props
    }) => {
        projects = projects || [project]
        const budgetData = FetchStore.getResponse('budgetTimeEntries' + endDate)
            ?.budgets?.[project?.id]?.[phase?.id]
        let budgetOptions = [
            'phaseBudgetForTeam',
            'phaseBudgetForRole',
            'phaseBudgetForStaff',
            'monthlyPhaseBudgetForStaff',
            'weeklyPhaseBudgetForStaff',
        ].filter((o) => o)
        const [selectedBudget, setSelectedBudget] = useState(budget)
        const budgetOptionsWithData = budgetOptions.filter((b) => {
            return (
                budgetLookup[b]?.({
                    projects,
                    project,
                    phase,
                    staff,
                    role,
                    startDate,
                    endDate,
                    extraHours,
                })?.denominator > 0
            )
        })

        const defaultBudget =
            selectedBudget ||
            budgetOptionsWithData?.[budgetOptionsWithData?.length - 1] ||
            'phaseBudgetForTeam'
        return (
            <div style={containerStyles} {...props}>
                {dropdownAbove && (
                    <Selector
                        style={{
                            zIndex: 100,
                            width: '100%',
                            fontSize: '1rem',
                            ...dropdownStyles,
                        }}
                        selectedOption={defaultBudget}
                        onChange={(v) => {
                            setSelectedBudget(v)
                            setBudget && setBudget(v)
                        }}
                        className={`budget-selector ${dropdownClassName || ''}`}
                        options={budgetOptions}
                        optionLabel={(bOpt) => {
                            return budgetNames[bOpt]
                        }}
                        variant="secondary"
                    />
                )}
                <RenderOnQueries
                    queryIds={budgetDataRequirements({
                        projects: projects || [project],
                        phase,
                        staff,
                        role,
                        startDate,
                        endDate,
                        extraHours,
                    })}
                >
                    <BudgetProgressBar
                        {...{
                            projects,
                            project,
                            phase,
                            staff,
                            role,
                            startDate,
                            endDate,
                            extraHours,
                            selectedBudget: defaultBudget,
                            barStyles,
                            textStyles,
                            variant: variant,
                        }}
                    />
                </RenderOnQueries>

                {!dropdownAbove && (
                    <Selector
                        style={{
                            zIndex: 1000,
                            width: '100%',
                            ...dropdownStyles,
                        }}
                        selectedOption={defaultBudget}
                        onChange={(v) => {
                            setSelectedBudget(v)
                            setBudget && setBudget(v)
                        }}
                        className={'budget-selector'}
                        options={budgetOptions}
                        optionLabel={(bOpt) => {
                            return budgetNames[bOpt]
                        }}
                        variant="secondary"
                    />
                )}
            </div>
        )
    }
)

const BudgetProgressBar = observer(
    ({
        projects,
        project,
        phase,
        staff,
        role,
        startDate,
        endDate,
        extraHours = 0,
        selectedBudget,
        barStyles,
        textStyles,
        variant,
    }) => {
        const budgetUse = budgetLookup[selectedBudget]({
            projects,
            project,
            phase,
            staff,
            role,
            startDate,
            endDate,
            extraHours,
        })
        const budgetProgress = budgetUse.numerator / budgetUse.denominator
        let progressGradient = { '0%': '#108ee9' }
        progressGradient[`${Math.round((0.5 / budgetProgress) * 100)}%`] =
            '#87d068'
        if (budgetProgress > 0.8) {
            progressGradient[`${Math.round((0.8 / budgetProgress) * 100)}%`] =
                '#ffc200'
        }
        if (budgetProgress > 1) {
            progressGradient[`${Math.round((1 / budgetProgress) * 100)}%`] =
                '#ff5800'
        }
        function makeCSSGradient(colorStops) {
            // Start the gradient string with the direction
            let gradient = 'linear-gradient(to right'

            // Add each color stop to the gradient string
            for (const position in colorStops) {
                gradient += `, ${colorStops[position]} ${position}`
            }

            // Close the gradient string
            gradient += ')'

            return gradient
        }
        return (
            <>
                <div
                    className="text-[#666] text-[13px] px-0 pb-2"
                    style={{
                        ...textStyles,
                    }}
                >
                    {`${FormatNumber(budgetUse.numerator)} / ${FormatNumber(
                        budgetUse.denominator
                    )}${
                        isFinite(budgetProgress)
                            ? ` (${FormatPercent(budgetProgress)})`
                            : ''
                    }`}
                </div>
                <Progress
                    value={budgetProgress * 100}
                    // showInfo={false}
                    fill={makeCSSGradient(progressGradient)}
                    // trailColor="#dddddd"
                    // style={{ padding: '0 0.75rem', ...barStyles }}
                />
                {variant === 'secondary' ? <div className="mb-1" /> : null}
            </>
        )
    }
)
